import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Currency, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { BoxLink, LabelPosition } from '@sorare/core/src/atoms/ui/Box';
import {
  FOOTBALL_PLAY_RIVALS_ARENA,
  FOOTBALL_SCOUTING_CENTER,
  INVITE,
} from '@sorare/core/src/constants/__generated__/routes';
import { ANY_SPORT_BLOG } from '@sorare/core/src/constants/routes';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { zeroMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';
import { BackgroundAligned } from '@sorare/gameplay/src/components/rivals/GridBackground/BackgroundAligned';

import {
  ForYouBlock_config,
  ForYouBlock_footballRivalsRoot,
} from './__generated__/index.graphql';
import blog from './assets/blog.png';
import blogMobile from './assets/blogmobile.jpg';
import referral from './assets/referral.png';
import referralMobile from './assets/referralmobile.jpg';
import scoutUrl from './assets/scout.png';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--double-unit);

  & > * {
    height: 200px;
  }
`;
const BoxWithBorder = styled(BoxLink)`
  padding: 0;
  background:
    var(--background) padding-box,
    var(--c-border) border-box;
  background-size: cover;
  background-position: center;
  border: 2px solid transparent;
  overflow: hidden;
`;

const RivalsBox = styled(BoxWithBorder)`
  --background: linear-gradient(var(--c-black), var(--c-black));
  --c-border: linear-gradient(
    to bottom right,
    var(--c-rivals-arena) 0%,
    #ff6bac 100%
  );
  isolation: isolate;
`;
const BlogBox = styled(BoxWithBorder)`
  --background: url(${blogMobile});
  --c-border: linear-gradient(
    to bottom right,
    #2a57fa 0%,
    var(--c-rivals-friendlies) 100%
  );
`;

const ScoutBox = styled(BoxWithBorder)`
  --light-green: #bff6d6;
  --dark-green: #048a3d;
  --background: url(${scoutUrl});
  --c-border: linear-gradient(
    to bottom right,
    var(--light-green),
    var(--dark-green)
  );
  background-repeat: no-repeat;
  background-size: 115%, cover;
`;
const ReferralBox = styled(BoxWithBorder)`
  --background: url(${referralMobile});
  --c-border: linear-gradient(to bottom right, #8f1bed 0%, #391885 100%);
`;
const RivalsBackground = styled(GridBackground)`
  z-index: -1;
`;
const LabelWrapper = styled(Vertical)`
  height: 100%;
  align-items: flex-start;
  justify-content: flex-end;
  padding: var(--double-unit);
`;
const Lines = styled.div`
  display: grid;
  grid-template-columns: calc(6 * var(--unit)) 1fr;
  column-gap: var(--double-unit);

  /* not in the first column (2n +1) and not in the first row (-n + 2) */
  & > :not(:nth-child(2n + 1)):not(:nth-child(-n + 2)) {
    border-top: 1px solid var(--c-nd-150);
  }
`;
const RivalsLogoWrapper = styled(Horizontal).attrs({ center: true })`
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-150);
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
`;
const RivalsDivisionLogo = styled.img`
  width: calc(10 * var(--unit));
  height: calc(10 * var(--unit));

  @media ${laptopAndAbove} {
    width: var(--quadruple-unit);
    height: var(--quadruple-unit);
  }
`;
const ImgWrapper = styled(Horizontal).attrs({ center: true })`
  padding: var(--intermediate-unit) 0;
`;
const Img = styled.img`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
`;
const StyledLinkBox = styled(LinkBox)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: 1;
  transition: opacity 0.05s ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`;

type Props = {
  rivalsRoot: ForYouBlock_footballRivalsRoot;
  config: ForYouBlock_config;
  scoutingCenter?: boolean;
};
export const ForYouBlock = ({ rivalsRoot, config, scoutingCenter }: Props) => {
  const { formatMessage } = useIntl();
  const { currentManager } = rivalsRoot;
  const { divisionConfig } = currentManager || {};
  const { badgeUrl, title } = divisionConfig || {};
  const { main: referralAmount } = useAmountWithConversion({
    monetaryAmount:
      config.referralProgramConfig?.maxDiscount || zeroMonetaryAmount,
    primaryCurrency: Currency.FIAT,
    hideFractionDigits: true,
  });
  const isLaptop = useIsLaptop();

  if (!isLaptop) {
    return (
      <Grid>
        {scoutingCenter && (
          <ScoutBox
            labelProps={{
              value: formatMessage({
                id: 'ScoutingCenterTile.title',
                defaultMessage: 'Scouting Center',
              }).toUpperCase(),
              bgColor: 'var(--dark-green)',
              position: LabelPosition.LEFT,
            }}
            to={generatePath(FOOTBALL_SCOUTING_CENTER)}
          >
            <LabelWrapper>
              <LabelM bold>
                <FormattedMessage
                  id="ScoutingCenterTile.cta.text"
                  defaultMessage="Scout Now"
                />
              </LabelM>
            </LabelWrapper>
          </ScoutBox>
        )}
        <RivalsBox
          labelProps={{
            value: formatMessage(glossary.daily).toUpperCase(),
            bgColor: 'var(--c-rivals-arena)',
            position: LabelPosition.LEFT,
          }}
          to={FOOTBALL_PLAY_RIVALS_ARENA}
        >
          <RivalsBackground
            height="100%"
            customGradient="linear-gradient(transparent, rgba(var(--c-rgb-rivals-arena), 0.5)), radial-gradient(transparent 0%, var(--c-black) 80%)"
          >
            <BackgroundAligned>
              <RivalsDivisionLogo src={badgeUrl} alt="" />
            </BackgroundAligned>
            <LabelWrapper>
              <LabelM bold>
                <FormattedMessage {...navLabels.rivals} />
              </LabelM>
            </LabelWrapper>
          </RivalsBackground>
        </RivalsBox>
        <BlogBox
          labelProps={{
            value: formatMessage(navLabels.blog).toUpperCase(),
            bgColor: '#2A57FA',
            position: LabelPosition.LEFT,
          }}
          to={generatePath(ANY_SPORT_BLOG, {
            sport: Sport.FOOTBALL,
          })}
        >
          <LabelWrapper>
            <LabelM bold>
              <FormattedMessage {...glossary.latestNews} />
            </LabelM>
          </LabelWrapper>
        </BlogBox>
        <ReferralBox
          labelProps={{
            value: formatMessage(glossary.referral).toUpperCase(),
            bgColor: '#8F1BED',
            position: LabelPosition.LEFT,
          }}
          to={INVITE}
        >
          <LabelWrapper>
            <LabelM bold>
              <FormattedMessage
                id="PlayPage.mobileReferralTitle"
                defaultMessage="Refer & Earn {value}"
                values={{ value: referralAmount }}
              />
            </LabelM>
          </LabelWrapper>
        </ReferralBox>
      </Grid>
    );
  }

  return (
    <Vertical gap={0.5}>
      <LabelL bold>
        <FormattedMessage id="PlayPage.forYou" defaultMessage="For You" />
      </LabelL>
      <Lines>
        {scoutingCenter && (
          <>
            <ImgWrapper>
              <Img src={scoutUrl} alt="" />
            </ImgWrapper>
            <StyledLinkBox>
              <Vertical gap={0}>
                <LabelM bold>
                  <FormattedMessage
                    id="ScoutingCenterTile.title"
                    defaultMessage="Scouting Center"
                  />
                </LabelM>
              </Vertical>
              <LinkOverlay
                as={Link}
                to={generatePath(FOOTBALL_SCOUTING_CENTER)}
              >
                <Button asSpan size="compact" color="quaternary">
                  <FormattedMessage
                    id="ScoutingCenterTile.cta.text"
                    defaultMessage="Scout Now"
                  />
                </Button>
              </LinkOverlay>
            </StyledLinkBox>
          </>
        )}

        <ImgWrapper>
          <RivalsLogoWrapper>
            <RivalsDivisionLogo src={badgeUrl} alt="" />
          </RivalsLogoWrapper>
        </ImgWrapper>
        <StyledLinkBox>
          <Vertical gap={0}>
            <LabelM bold>
              <FormattedMessage {...navLabels.rivals} />
            </LabelM>
            <LabelM color="var(--c-nd-600)">{title}</LabelM>
          </Vertical>
          <LinkOverlay as={Link} to={FOOTBALL_PLAY_RIVALS_ARENA}>
            <Button asSpan size="compact" color="quaternary">
              <FormattedMessage {...glossary.playNow} />
            </Button>
          </LinkOverlay>
        </StyledLinkBox>
        <ImgWrapper>
          <Img src={blog} alt="" />
        </ImgWrapper>
        <StyledLinkBox>
          <Vertical gap={0}>
            <LabelM bold>
              <FormattedMessage {...glossary.latestNews} />
            </LabelM>
            <LabelM color="var(--c-nd-600)">
              <FormattedMessage {...navLabels.blog} />
            </LabelM>
          </Vertical>
          <LinkOverlay
            as={Link}
            to={generatePath(ANY_SPORT_BLOG, {
              sport: Sport.FOOTBALL,
            })}
          >
            <Button asSpan size="compact" color="quaternary">
              <FormattedMessage
                id="PlayPage.blog.cta"
                defaultMessage="Read now"
              />
            </Button>
          </LinkOverlay>
        </StyledLinkBox>
        <ImgWrapper>
          <Img src={referral} alt="" />
        </ImgWrapper>
        <StyledLinkBox>
          <Vertical gap={0}>
            <LabelM bold>
              <FormattedMessage
                id="PlayPage.referralTitle"
                defaultMessage="Invite a friend, earn {value}"
                values={{ value: referralAmount }}
              />
            </LabelM>
            <LabelM color="var(--c-nd-600)">
              <FormattedMessage
                id="PlayPage.referralSubtitle"
                defaultMessage="In referral credits"
              />
            </LabelM>
          </Vertical>
          <LinkOverlay as={Link} to={INVITE}>
            <Button asSpan size="compact" color="quaternary">
              <FormattedMessage
                id="ReferralSpecialEvents.learnMore"
                defaultMessage="Invite friends"
              />
            </Button>
          </LinkOverlay>
        </StyledLinkBox>
      </Lines>
    </Vertical>
  );
};

ForYouBlock.fragments = {
  footballRivalsRoot: gql`
    fragment ForYouBlock_footballRivalsRoot on FootballRivalsRoot {
      id
      currentManager {
        id
        divisionConfig {
          id
          badgeUrl
          title
        }
      }
    }
  ` as TypedDocumentNode<ForYouBlock_footballRivalsRoot>,
  config: gql`
    fragment ForYouBlock_config on Config {
      id
      referralProgramConfig(sport: FOOTBALL) {
        id
        maxDiscount {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<ForYouBlock_config>,
};
